/**
 * Email domain classification for Spring Fertility.
 *
 * Used by the reassignment guardrail to tell an OUTSIDE recipient (a patient or
 * their partner) apart from a colleague. Reassigning an outside recipient takes
 * over their signing slot and they lose access to the envelope; reassigning a
 * colleague is routine coverage handoff and must not be interrupted.
 *
 * WHY THIS RULE, measured against 365 successful production reassignments in
 * dbo.ReassignmentLogs (see C:\CC\DocuSpa\2026-07-30_Reassignment-Guardrail):
 *
 *   "current recipient is external"   ->  fires 4 times, 0 false positives
 *   "source and destination domains differ" -> fires 10 times, 3 of them on
 *                                        LEGITIMATE internal handoff between
 *                                        two different Spring domains
 *
 * Spring owns four domains, so a naive domain-mismatch comparison treats
 * springfertility.com -> openfertility.com as suspicious when it is ordinary
 * staff-to-staff handoff. That is why classification is against an explicit
 * internal-domain list rather than a string comparison of the two addresses.
 */

/**
 * Spring Fertility owned domains (single tenant 160e3c6d-fc94-412a-ab1e-cad2e4812e16).
 * Source of truth: C:\CC\CLAUDE.md.
 *
 * IMPORTANT: adding a Spring domain here without updating the matching list in
 * DSWebhookAPI/Services/EmailDomainClassifier.cs will make the UI and the
 * server-side detection disagree.
 */
export const SPRING_DOMAINS = Object.freeze([
  'springfertility.com',
  'openfertility.com',
  'genesis-fertility.com',
  'nestcryobank.com',
]);

/**
 * True when the address does NOT belong to a Spring-owned domain, i.e. the
 * recipient is an outside party (patient, partner, third party).
 *
 * Fails SAFE: a null, empty or malformed address returns true, so the guardrail
 * warns rather than staying silent on input it cannot classify.
 *
 * Matching is suffix-anchored on '@' + domain, so a lookalike domain such as
 * notspringfertility.com or springfertility.com.evil.tld is correctly external.
 *
 * @param {string|null|undefined} email
 * @returns {boolean}
 */
export function isExternalRecipient(email) {
  if (typeof email !== 'string') return true;

  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');

  // No '@', leading '@', or trailing '@' -> not a classifiable address.
  if (atIndex <= 0 || atIndex === normalized.length - 1) return true;

  const domain = normalized.slice(atIndex + 1);
  return !SPRING_DOMAINS.includes(domain);
}

/**
 * Convenience inverse, for readability at call sites.
 * @param {string|null|undefined} email
 * @returns {boolean}
 */
export function isSpringRecipient(email) {
  return !isExternalRecipient(email);
}
